import React, { useMemo } from "react";

// Decorative animated flight map for the auth pages: planes flying between
// major airports. Purely visual (aria-hidden, no pointer events).
// Styles: "Flight routes background" block in App.css.
// `variant="panel"` is the dimmer version that sits inside the brand panel.

const W = 1600;
const H = 800;
const LON = [-10, 130];
const LAT = [-10, 60];

const project = (lon, lat) => [
  ((lon - LON[0]) / (LON[1] - LON[0])) * W,
  ((LAT[1] - lat) / (LAT[1] - LAT[0])) * H,
];

const AIRPORTS = {
  DEL: { lon: 77.1, lat: 28.56 },
  BOM: { lon: 72.87, lat: 19.09 },
  BLR: { lon: 77.7, lat: 13.2 },
  HYD: { lon: 78.43, lat: 17.24 },
  CCU: { lon: 88.44, lat: 22.65 },
  MAA: { lon: 80.17, lat: 12.99 },
  DXB: { lon: 55.36, lat: 25.25 },
  LHR: { lon: -0.45, lat: 51.47 },
  SIN: { lon: 103.99, lat: 1.36 },
  BKK: { lon: 100.75, lat: 13.69 },
  HKG: { lon: 113.9, lat: 22.3 },
};

// [from, to, seconds, offset seconds]
const ROUTES = [
  ["DEL", "BOM", 7, 0],
  ["BLR", "DEL", 8, 2],
  ["BOM", "CCU", 9, 4],
  ["HYD", "DEL", 6.5, 1],
  ["MAA", "BOM", 6, 3],
  ["DEL", "DXB", 9, 5],
  ["LHR", "DEL", 16, 3],
  ["DXB", "LHR", 14, 8],
  ["BOM", "SIN", 12, 6],
  ["DEL", "BKK", 11, 1],
  ["CCU", "HKG", 10, 7],
  ["BLR", "SIN", 9, 2],
];

const buildRoute = ([from, to, dur, delay]) => {
  const [x1, y1] = project(AIRPORTS[from].lon, AIRPORTS[from].lat);
  const [x2, y2] = project(AIRPORTS[to].lon, AIRPORTS[to].lat);
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const len = Math.hypot(x2 - x1, y2 - y1);
  // bow the arc upward, perpendicular to the straight line
  const nx = -(y2 - y1) / len;
  const ny = (x2 - x1) / len;
  const sign = ny > 0 ? -1 : 1;
  const bow = Math.min(len * 0.22, 110);
  const cx = mx + nx * sign * bow;
  const cy = my + ny * sign * bow;
  return {
    key: `${from}-${to}`,
    d: `M${x1.toFixed(1)} ${y1.toFixed(1)} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`,
    dur,
    delay,
  };
};

const FlightRoutesBackground = ({ variant = "page" }) => {
  const routes = useMemo(() => ROUTES.map(buildRoute), []);

  return (
    <svg
      className={`flight-bg flight-bg--${variant}`}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {routes.map((r) => (
        <path key={`track-${r.key}`} className="flight-track" d={r.d} />
      ))}

      {routes.map((r) => (
        <path
          key={`comet-${r.key}`}
          className="flight-comet"
          d={r.d}
          pathLength="100"
          style={{
            animationDuration: `${r.dur}s`,
            animationDelay: `-${r.delay}s`,
          }}
        />
      ))}

      {Object.entries(AIRPORTS).map(([code, { lon, lat }]) => {
        const [x, y] = project(lon, lat);
        return (
          <g key={code} transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
            <circle className="flight-pulse" r="5" />
            <circle className="flight-node" r="4.5" />
            <text className="flight-label" x="11" y="5">
              {code}
            </text>
          </g>
        );
      })}

      {routes.map((r) => (
        <g key={`plane-${r.key}`} className="flight-plane">
          <path d="M14 0 L-9 -8 L-4 0 L-9 8 Z" />
          <animateMotion
            dur={`${r.dur}s`}
            begin={`-${r.delay}s`}
            repeatCount="indefinite"
            rotate="auto"
            path={r.d}
          />
        </g>
      ))}
    </svg>
  );
};

export default FlightRoutesBackground;
